import {
	formatProductCard,
	type ProductCard,
	type ProductDetails,
	type RGProduct,
} from "@raumagarn/formatters";

export const getProductBreadcrumbs = (product: RGProduct) => {
	try {
		const fullCategoryUri = product.categoryUri;
		const categoryUris = fullCategoryUri?.split("/");
		const categoryNames = product.categoryName;

		if (!fullCategoryUri || !categoryUris || !categoryNames) return [];

		const breadcrumbs = [
			{
				uri: `/produkter/${categoryUris?.[0]}`,
				name: categoryNames?.[0],
			},
			{
				uri: product.uri,
				name: product.name,
			},
		];
		return breadcrumbs;
	} catch {
		return undefined;
	}
};

export const getIssuuURL = (product: RGProduct) => {
	const url = product.pr_issuu_url_length;
	if (!url) return undefined;

	const [, slug] = url.split("https://issuu.com/rorostweed/docs/");
	if (!slug) {
		return undefined;
	}

	const issuuURL = `https://e.issuu.com/embed.html?d=${slug}&u=rorostweed`;

	return issuuURL;
};

export const getYarnRelation = (product: RGProduct) => {
	const relatedProducts = product?.relatedProducts || [];

	const yarns = relatedProducts?.filter(
		(relatedProduct) => relatedProduct.relation === "Yarn in recipe",
	);

	const productSku = new Set(yarns?.map((yarn) => (yarn as RGProduct).productSku));
	const uniqueYarns = Array.from(productSku);
	const uniqueYarnsIds = uniqueYarns.map((yarn) => {
		const tempYarn = yarns?.find((y) => (y as RGProduct).productSku === yarn);
		return {
			id: tempYarn?.product,
			centraProductId: (tempYarn as RGProduct | undefined)?.centraProduct,
			name: (tempYarn as RGProduct | undefined)?.name,
		};
	});

	return uniqueYarnsIds;
};

const formatMeasurementChart = (chart: RGProduct["measurementChart"]) => {
	if (!chart) return undefined;
	const { contents = [], x = [], y = [] } = chart;
	const formattedChart = [];

	for (let i = 0; i < y.length; i++) {
		const row = { size: y[i] } as {
			size: string;
			[measurement: string]: string;
		};
		for (let j = 0; j < x.length; j++) {
			const measurement = x[j];
			const value = contents.find((content) => content.x === j && content.y === i)?.content;
			if (value) {
				row[measurement!] = value;
			}
		}
		formattedChart.push(row);
	}

	return { chart: formattedChart, keys: x };
};

export const getSizeChart = (product: RGProduct) => {
	try {
		const measurementChart = product.measurementChart;
		if (!measurementChart) return;
		const sizeGuide = formatMeasurementChart(measurementChart);
		return sizeGuide;
	} catch {
		return undefined;
	}
};
export const generateVariants = (product: ProductCard) => {
	const variants = product.relatedProducts
		? product.relatedProducts
				.filter((variant) => variant.relation === "variant")
				.map((variant) => {
					const formattedVariant = formatProductCard(variant as unknown as RGProduct);
					const name = formattedVariant.variantName;
					const id = product?.centraVariant?.toString() || "";
					const sku = getVariantSku(product);
					const weight = formattedVariant.weight;
					const size = getOneSize(formattedVariant);
					const length = formattedVariant.length;

					const firstThumbnail = variant.media?.at(0)?.href; // TODO: Make sure to use thumb-4x5
					const secondThumbnail = variant.media?.at(1)?.href; // TODO: Make sure to use thumb-4x5
					const items = variant.items;
					const displayItemId = variant.displayItemId;
					const variantName = variant.variantName;
					const pricelist = variant.pricelist;

					return {
						name,
						id,
						weight,
						firstThumbnail,
						secondThumbnail,
						sku,
						length,
						size,
						items,
						item: variant.id,
						displayItemId,
						variantName,
						pricelist,
					};
				})
		: [];

	const defaultVariant = {
		name: product.variantName,
		id: product?.centraVariant?.toString() || "",
		weight: product.weight,
		firstThumbnail: product.media?.at(0)?.href, // TODO: Make sure to use thumb-4x5
		secondThumbnail: product.media?.at(1)?.href, // TODO: Make sure to use thumb-4x5
		sku: product.sku,
		length: product.length,
		size: getOneSize(product),
		items: product.items,
		item: product.id,
		displayItemId: product.displayItemId,
		pricelist: product.pricelist,
		variantName: product.variantName,
	};

	variants.unshift(defaultVariant);

	return variants;
};

export const getOneSize = (product: ProductCard | RGProduct) => {
	const sizes = product?.items;

	const formattedSizes = sizes?.map((size) => {
		return {
			...size,
			id: size.item,
			stock: size.stock === "yes",
			fewLeft: size.stock === "few",
		};
	});
	if (product?.items?.length === 1) return formattedSizes?.[0];

	throw new Error("Product has more than one size");
};

const getVariantSku = (product: ProductCard) => {
	const stripProductNumber = (productNumber?: string) => {
		if (!productNumber) {
			console.error("No product number provided");
			return productNumber;
		}
		const variantNumber =
			productNumber?.split("-").length > 1 ? productNumber.split("-")[1] : productNumber.slice(3);
		return variantNumber;
	};

	const productSku = product.sku;
	const variantSku = stripProductNumber(productSku);
	return variantSku;
};

export const getSizes = (product: RGProduct) => {
	const sizes = product.items;
	const formattedSizes = sizes?.map((size) => {
		return {
			...size,
			id: size.item,
			stock: size.stock === "yes",
			fewLeft: size.stock === "few",
		};
	});

	return formattedSizes;
};
export const getRelatedProducts = (product: RGProduct) => {
	if (Object.hasOwn(product, "relatedProducts")) {
		const relatedProducts = product?.relatedProducts;

		return relatedProducts;
	}
	return [];
};

export const getBooklet = (product: ProductCard) => {
	if (Object.hasOwn(product, "relatedProducts")) {
		const bookletRaw = product.relatedProducts?.find((r) => r.relation === "Recipe in booklet");

		if (!bookletRaw) return undefined;

		const name = bookletRaw.name;
		const uri = bookletRaw.uri;
		const id = product.id;
		const media = bookletRaw.media;
		const firstThumbnail = bookletRaw.media?.at(0)?.href; // TODO: Make sure to use thumb-4x5
		const size = getOneSize(bookletRaw as ProductCard);
		const variantName = bookletRaw.variantName;
		const fits = bookletRaw.fits;
		const sku = getVariantSku(bookletRaw as ProductCard);
		const pricelist = bookletRaw.pricelist;
		const displayItemId = bookletRaw.displayItemId;

		return {
			name,
			uri,
			id,
			media,
			firstThumbnail,
			size,
			variantName,
			fits,
			sku,
			pricelist,
			displayItemId,
		};
	}
	return undefined;
};

export const getNeedleItemsFromSizes = (product: ProductDetails) => {
	const items = (product.sizes || []).map((s) => {
		const name = product.name;
		const uri = product.uri;
		const firstThumbnail = product.media?.at(0)?.href;
		const needle = product.needles;
		const size = {
			data: {
				name: s.name,
				id: s.id,
				stock: s.stock,
				fewLeft: s.fewLeft,
			},
		};
		const length = product.length;

		return {
			name,
			uri,
			needle,
			size,
			firstThumbnail,
			length,
			pricelist: product.pricelist,
		};
	});

	return items;
};
